import { css } from 'lit';

/** Base styles for shadow roots: global CSS does not reach inside them. */
export const shared = css`
  :host {
    display: block;
  }
  h1,
  h2,
  h3 {
    font-family: var(--ka-font-display);
    font-weight: 600;
    line-height: 1.2;
    margin: 0 0 0.5em;
    letter-spacing: 0.02em;
    text-wrap: pretty;
  }
  h1 {
    font-size: clamp(1.5rem, 6vw, 3rem);
  }
  h2 {
    font-size: clamp(1.35rem, 3.5vw, 2rem);
  }
  p {
    margin: 0 0 1em;
  }
  a {
    color: var(--ka-gold);
  }
  a:hover {
    color: var(--ka-parchment);
  }
  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
  }
  /* Phones: buttons in a row share the line, and once they wrap each one fills its line,
     so a stack is always one even width. */
  @media (max-width: 599px) {
    :is(.row, .ctas, .actions) > .btn {
      flex: 1 1 auto;
      justify-content: center;
      /* never squeeze a label onto two lines: move the button to the next line instead */
      min-width: -webkit-fit-content;
      min-width: fit-content;
    }
  }
  /* The games' more-actions (⋯) menu: one full-width button per line, and a visible arrow on
     its fold-outs (the menu hides its own summary marker, which also hid theirs). */
  .more .more-body .row {
    display: grid;
    gap: 0.5rem;
  }
  .more .more-body .row > .btn {
    justify-content: center;
  }
  .more .more-body details > summary {
    color: var(--ka-gold);
  }
  .more .more-body details > summary::before {
    content: '▸';
    margin-right: 0.5em;
  }
  .more .more-body details[open] > summary::before {
    content: '▾';
  }
  :focus-visible {
    outline: 3px solid var(--ka-focus);
    outline-offset: 2px;
  }
  .btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5em;
    box-sizing: border-box; /* links and labels as tall as real buttons */
    min-height: 44px;
    padding: 0.6em 1.2em;
    border: 2px solid var(--ka-edge);
    border-radius: var(--ka-radius);
    background: var(--ka-stone-600);
    color: var(--ka-parchment);
    box-shadow: var(--ka-bevel);
    font: 600 1rem var(--ka-font-display);
    text-decoration: none;
    cursor: pointer;
  }
  .btn:hover {
    background: var(--ka-stone-500);
    color: var(--ka-parchment);
  }
  .btn.primary {
    background: var(--ka-red-deep);
  }
  .btn.primary:hover {
    background: var(--ka-red-deeper);
  }
  .btn.green {
    background: var(--ka-foliage-dim);
  }
  .btn:active:not([disabled]) {
    box-shadow: var(--ka-bevel-pressed);
  }
  /* LinkButton: looks like a link, behaves like a button. */
  button.link {
    all: unset;
    cursor: pointer;
    color: var(--ka-gold);
    text-decoration: underline;
    min-height: 44px;
    min-width: 44px;
    display: inline-flex;
    align-items: center;
  }
  button.link:focus-visible {
    outline: 3px solid var(--ka-focus);
  }
  .btn[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .panel {
    background: var(--ka-stone-800);
    border: 2px solid var(--ka-edge);
    box-shadow: var(--ka-bevel);
    padding: 1.25rem;
    position: relative;
  }
  .muted {
    color: var(--ka-parchment-dim);
  }
  .err {
    color: var(--ka-red-bright);
  }
  /* Field help, inside the <label> under its text. */
  .hint {
    font-weight: 400;
    font-size: 0.85rem;
    color: var(--ka-parchment-dim);
  }
  [aria-invalid='true'] {
    border-color: var(--ka-red-bright) !important;
  }
  .tag {
    display: inline-block;
    font: 600 0.8rem var(--ka-font-display);
    padding: 0.15em 0.5em;
    border: 1px solid currentColor;
    border-radius: var(--ka-radius);
  }
  .fixture {
    color: var(--ka-ink-on-gold);
    background: var(--ka-gold);
    border-color: var(--ka-edge);
  }
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
  }
  .grid {
    display: grid;
    gap: var(--ka-gap);
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 16rem), 1fr));
  }
`;
